import { Injectable } from '@angular/core';
import { RecursivePartial } from '@models/apollo';
import { CartItem, Order, OrderItem, Product, PromoCodeUse, ShoppingCart } from '@queries/generated';
import { Angulartics2GoogleTagManager } from 'angulartics2';
import { map } from 'rxjs/operators';
import { StorefrontsService } from './storefronts.service';

@Injectable({
  providedIn: 'root'
})
export class EcommerceTrackingService {
  currency: string;
  affiliation: string;

  constructor(private gtm: Angulartics2GoogleTagManager, private storefronts: StorefrontsService) {
    this.storefronts.current$
      .pipe(
        map((storefront) => ({
          affiliation: storefront.name,
          currency: storefront.currency.isoCode
        }))
      )
      .subscribe((params) => Object.assign(this, params));
  }

  addToCart(cartItem: RecursivePartial<CartItem>) {
    const { currency } = this;
    const { price: value } = cartItem;
    const items = [this.buildCartData(cartItem)];

    this.gtm.pushLayer({ ecommerce: null });
    this.gtm.pushLayer({ event: 'add_to_cart', ecommerce: { currency, value, items } });
  }

  beginCheckout(shoppingCart: ShoppingCart) {
    const { currency } = this;
    const { expectedTotal: value } = shoppingCart;
    const items = shoppingCart.cartItems.map((cartItem) => this.buildCartData(cartItem));

    this.gtm.pushLayer({ ecommerce: null });
    this.gtm.pushLayer({ event: 'begin_checkout', ecommerce: { currency, value, items } });
  }

  purchase(order: RecursivePartial<Order>) {
    const { affiliation, currency } = this;
    const { id: transaction_id, total: value, tax, shipping } = order;
    const items = order.orderItems.map((orderItem) => this.buildOrderData(orderItem, order.promoCodeUses));

    this.gtm.pushLayer({ ecommerce: null });
    this.gtm.pushLayer({
      event: 'purchase',
      ecommerce: { currency, value, items, transaction_id, affiliation, tax, shipping }
    });
  }

  removeFromCart(cartItem: RecursivePartial<CartItem>) {
    const { currency } = this;
    const { price: value } = cartItem;
    const items = [this.buildCartData(cartItem)];

    this.gtm.pushLayer({ ecommerce: null });
    this.gtm.pushLayer({ event: 'remove_from_cart', ecommerce: { currency, value, items } });
  }

  viewItem(item: RecursivePartial<Product>) {
    // maybe when we introduce a "product page" in venom
    const { currency } = this;
    const { price: value } = item;
    const items = [this.buildProductData(item)];

    this.gtm.pushLayer({ ecommerce: null });
    this.gtm.pushLayer({ event: 'view_item', ecommerce: { currency, value, items } });
  }

  viewItemList(products: RecursivePartial<Product>[], item_list_name = 'venom-catalog') {
    const items = products.map((product) => this.buildProductData(product));

    this.gtm.pushLayer({ ecommerce: null });
    this.gtm.pushLayer({ event: 'view_item_list', ecommerce: { item_list_name, items } });
  }

  private buildCartData(item: RecursivePartial<CartItem>) {
    const { quantity = 1, price, product } = item;
    const { brand: item_brand = '', sku: item_id = '', name: item_name, price: fullPrice } = product;

    return {
      item_id,
      item_name,
      item_brand,
      price,
      quantity,
      discount: fullPrice - price || 0,
      coupon: item.promoCode?.code || ''
    };
  }

  private buildOrderData(item: RecursivePartial<OrderItem>, promoCodeUses: RecursivePartial<PromoCodeUse[]>) {
    const { quantity = 1, price, product } = item;
    const { brand: item_brand = '', sku: item_id = '', name: item_name, price: fullPrice } = product;
    const { 0: promoCodeUse } = promoCodeUses.filter((promoCodeUse) => item.id === promoCodeUse.orderItem.id);

    return {
      item_id,
      item_name,
      item_brand,
      price,
      quantity,
      discount: fullPrice - price || 0,
      coupon: promoCodeUse?.promoCode?.code || ''
    };
  }

  private buildProductData(item: RecursivePartial<Product>) {
    const { brand: item_brand = '', sku: item_id = '', name: item_name, price } = item;

    return {
      item_id,
      item_name,
      item_brand,
      price,
      quantity: 1,
      discount: 0,
      coupon: ''
    };
  }
}
